import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ButtonDropdown, Icon, Input, Modal, Status, Table, Tag } from '@bitcine/cinesend-theme'
import useStore from '../../hooks/use_store'
import { useNavigate } from 'react-router-dom'
import useTableFilters from '../../hooks/use_table_filters'
import { useState } from 'react'

const SchemasTable = ({ queryKey }) => {
  const { createModel, deleteModel } = useStore()
  const { data: airlines, error: airlinesError, isLoading: airlinesLoading } = useQuery(['api/airlines-list'])

  const filteringOptions = [
    {
      label: 'State',
      key: 'state',
      type: 'checkboxes',
      options: [
        { label: 'Locked', key: 'locked' },
        { label: 'Unlocked', key: 'unlocked' },
      ],
    },
    {
      label: 'Airline',
      key: 'airline',
      type: 'checkboxes',
      options: airlines?.map((airline) => ({ label: airline.name, key: airline.id })),
    },
  ]
  const { queryString, searching, filtering, sorting, pagination } = useTableFilters({
    filteringOptions,
    enableSearch: true,
  })
  const { data: schemas, error, isLoading } = useQuery([queryKey, queryString])
  const navigate = useNavigate()
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [originSchemaID, setOriginSchemaID] = useState('')
  const [newSchemaName, setNewSchemaName] = useState('')
  const queryClient = useQueryClient()

  const renderAirlinesTags = (airlines) => {
    return (
      <div className='flex flex-wrap gap-1'>
        {airlines.map((airline) => (
          <Tag label={airline} size='small' type='neutral' outline={true} key={airline} />
        ))}
      </div>
    )
  }

  return (
    <div>
      <Table
        status={{
          pending: isLoading,
          error: error,
          errorMessage: error?.message,
        }}
        widths={[200, 'auto', 100, 80, 65]}
        header={{
          columns: [
            { text: 'Name', key: 'name' },
            { text: 'Airlines', key: 'airline_name' },
            { text: 'Cycles', key: 'cycles' },
            { text: 'Locked', key: 'locked' },
            { text: '' },
          ],
          searching,
          filtering,
          pagination,
        }}
        body={{
          data: schemas?.data,
          row: {
            compact: true,
            spaced: true,
            onClick: (event, data) => navigate(`/settings/schemas/${data.id}`),
            render: [
              (data) => data.name,
              (data) => renderAirlinesTags(data.airlines),
              (data) => data.cycles,
              (data) => (data.is_locked ? <Icon icon='lock' /> : ''),
              (data) => (
                <ButtonDropdown
                  kebab
                  dropdown={{
                    content: [
                      {
                        text: 'Copy Schema',
                        onClick: () => {
                          setOriginSchemaID(data.id)
                          setNewSchemaName(data.name + ' (copy)')
                          setIsOpenModal(true)
                        },
                      },
                      {
                        text: 'Delete',
                        className: data.is_locked ? '' : 'text-red-500',
                        onClick: () =>
                          deleteModel.mutate(
                            { endpoint: 'schemas', id: data.id },
                            {
                              onSuccess: () => {
                                queryClient.invalidateQueries([queryKey, queryString])
                              },
                            }
                          ),
                      },
                    ],
                  }}
                />
              ),
            ],
          },
          empty: {
            title: 'No schemas found!',
            text: 'Clone an existing schema to begin.',
            icon: 'list_alt',
          },
        }}
      />
      {isOpenModal && (
        <Modal
          isOpen={isOpenModal}
          onClose={() => setIsOpenModal(false)}
          header='Copy Schema'
          pending={false}
          confirmButton={{
            text: 'Save',
            onClick: () => {
              createModel.mutate(
                {
                  endpoint: 'schemas',
                  data: { schemaID: originSchemaID, name: newSchemaName },
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries([queryKey, queryString])
                  },
                }
              )
              setIsOpenModal(false)
            },
          }}
        >
          <Input
            label='Schema Name'
            type='text'
            value={newSchemaName}
            onChange={(e) => setNewSchemaName(e.target.value)}
          />
        </Modal>
      )}
    </div>
  )
}

export default SchemasTable
